import Button from 'devextreme-react/button';
import DataGrid, { Column, Editing, Format, Lookup } from 'devextreme-react/data-grid';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { formatNumber } from 'devextreme/localization';
import { useContext, useState } from 'react';
import { showErrorMessage } from '../../../../utils/messages';
import { apiGetNewMaterialOrderProducts } from '../api/get-new-material-order-products';
import { MaterialOrderContext } from '../context/MaterialOrderContext';
import { calculateTotals } from '../functions/calculateTotals';
import { MOProduct } from '../types/MaterialOrderDetails';
import MaterialOrderProductPopup from './MaterialOrderProductPopup';
import { confirm } from 'devextreme/ui/dialog';
import { MOCatalogProduct } from '../types/MOCatalogProduct';
import { NewMOProduct } from '../types/NewMOProduct';
import ROLinkPopup from './ROLinkPopup';
import { validateDeliveryDate } from '../functions/deliveryDate';

export default function NewMaterialOrderProducts() {

    const { order, modifyOrder } = useContext(MaterialOrderContext);
    let [isProductPopupVisible, setProductPopupVisibility] = useState(false);
    let [isROLinkPopupVisible, setROLinkPopupVisibility] = useState(false);

    const products = order.details.products?.map(x => x) ?? [];

    return (
        <>
            <ROLinkPopup
                visible={isROLinkPopupVisible}
                onPopupHidden={(result) => {
                    if (result) {
                        setProductPopupVisibility(true);
                    }
                    setROLinkPopupVisibility(false);
                }}
            ></ROLinkPopup>

            <Toolbar className='button-toolbar'>
                <Item
                    visible={true}
                    location={'before'}
                >
                    <Button
                        text="Add Product"
                        icon="Inscyth-Add"
                        elementAttr={{ 'data-testid': 'btn-add-line' }}
                        onClick={() => {
                            if (order.details.salesChannelID) {
                                setProductPopupVisibility(true);
                            }
                            else {
                                setROLinkPopupVisibility(true);
                            }
                        }}>
                    </Button>
                </Item>
            </Toolbar>

            <MaterialOrderProductPopup
                visible={isProductPopupVisible}
                onPopupHidden={() => setProductPopupVisibility(false)}
                onProductsAdded={(newProducts: MOCatalogProduct[]) => {

                    let products = (order.details.products ?? []).map((x) => {
                        return {
                            ...x,
                            searchAdhesive: false
                        } as NewMOProduct
                    })
                        .concat(newProducts.map((x) => {
                            return {
                                productID: x.productID,
                                quantity: x.quantity,
                                pullFromStock: false,
                                productRequestedDeliveryDate: x.productRequestedDeliveryDate,
                                edgeDepthID: x.edgeDepthID,
                                lengthID: x.lengthID,
                                miterID: x.miterID,
                                backerID: x.backerID,
                                substrateID: x.substrateID,
                                searchAdhesive: true,
                                mfrSku: x.mfrModel,
                                productMFRSKUID: x.productMFRSKUID,
                                deliveryDateSettings: x.deliveryDateSettings
                            } as NewMOProduct
                        }));

                    apiGetNewMaterialOrderProducts(
                        order.details.shipToLocationID,
                        order.details.salesChannelID,
                        order.details.roDate,
                        products
                    )
                        .then((result) => {
                            if (result.isSuccess) {
                                var updatedProducts = result.products;
                                const totals = calculateTotals(updatedProducts, order.details.taxRate);
                                modifyOrder({
                                    ...order.details,
                                    subTotal: totals.subTotal,
                                    amount: totals.total,
                                    freightCharges: totals.freightCharges,
                                    cutCharges: totals.cutCharges,
                                    salesTax: totals.salesTax,
                                    taxAmount: totals.taxAmount,
                                    products: updatedProducts,
                                    productMFRSKUs: result.productMFRSKUs
                                });

                                if (result.matchingAdhesives.length > 0) {
                                    confirm('Do you want to add adhesive items?', 'Adhesive Items')
                                        .then((confirmed) => {
                                            if (confirmed) {
                                                updatedProducts = updatedProducts.concat(result.matchingAdhesives);
                                                modifyOrder({ ...order.details, products: updatedProducts }, true);
                                            }
                                        })
                                }
                            }
                            else {
                                showErrorMessage(result.errorMessage!);
                            }
                        });
                }}
            />

            <div className='dx-card wide-card'>
                <div className='grid-header'>Material Order Details</div>
                <DataGrid
                    className='grid-with-header'
                    dataSource={products}
                    allowColumnReordering={true}
                    allowColumnResizing={true}
                    showBorders={true}
                    showColumnLines={true}
                    columnAutoWidth={false}
                    wordWrapEnabled={true}
                    keyExpr={'moProductID'}
                    onRowRemoved={(e) => {
                        modifyOrder({ ...order.details, products: products }, true);
                    }}
                    onRowUpdated={(e) => {
                        modifyOrder({ ...order.details, products: products }, true);
                    }}
                    onEditorPreparing={(e) => {
                        if (e.dataField === 'productRequestedDeliveryDate') {
                            e.editorOptions.disabledDates = (args: any) => {
                                return !validateDeliveryDate(args.date, e.row?.data.deliveryDateSettings!);
                            }
                        }

                        if (e.dataField === 'productMFRSKUID') {
                            e.editorOptions.dataSource = order.details.productMFRSKUs
                                .filter(x => x.productID === e.row?.data.productID);
                        }
                    }}
                    onRowUpdating={(e) => {
                        if (!!e.newData.productRequestedDeliveryDate &&
                            e.newData.productRequestedDeliveryDate !== e.oldData.productRequestedDeliveryDate &&
                            !validateDeliveryDate(e.newData.productRequestedDeliveryDate, e.oldData.deliveryDateSettings)) {
                            e.cancel = true;
                            e.component.cancelEditData();
                        }
                    }}
                >
                    <Editing
                        mode="cell"
                        confirmDelete={true}
                        allowDeleting={true}
                        allowUpdating={true}
                    />
                    <Column
                        dataField={'productLine'}
                        caption={'Product Line'}
                        dataType="string"
                        allowEditing={false}
                    />
                    <Column
                        dataField={'subProductLine'}
                        caption={'Sub Product Line'}
                        dataType="string"
                        allowEditing={false}
                    />
                    <Column
                        dataField={'productType'}
                        caption={'Product Type'}
                        dataType="string"
                        allowEditing={false}
                    />
                    <Column
                        dataField={'sku'}
                        caption={'House PO SKU'}
                        dataType="string"
                        allowEditing={false}
                        width={80}
                    />
                    <Column
                        dataField={'productMFRSKUID'}
                        caption={'MFR SKU'}
                        dataType="number"
                        showEditorAlways={true}
                        width={100}
                        editorOptions={{
                            dropDownOptions: {
                                minWidth: 200,
                            }
                        }}
                    >
                        <Lookup
                            displayExpr={'mfrSku'}
                            valueExpr={'productMFRSKUID'}
                        />
                    </Column>
                    <Column
                        dataField={'mfrDescription'}
                        caption={'MFR Description'}
                        dataType="string"
                        allowEditing={false}
                        width={100}
                    />
                    <Column
                        dataField={'description'}
                        caption={'Description'}
                        dataType="string"
                        allowEditing={false}
                        width={100}
                        encodeHtml={false}
                        calculateDisplayValue={(rowData: MOProduct) =>
                            rowData.description.replace(new RegExp('\r\n', 'g'), '<br/>')
                        }
                    />
                    <Column
                        dataField={'thickness'}
                        caption={'Thickness'}
                        dataType="string"
                        allowEditing={false}
                    />
                    <Column
                        dataField={'dimensions'}
                        caption={'Dimensions'}
                        dataType="string"
                        allowEditing={false}
                    />
                    <Column
                        dataField={'squareFootage'}
                        caption={'Square Footage'}
                        dataType="string"
                        allowEditing={false}
                    />
                    <Column
                        dataField={'distributorName'}
                        caption={'Distributor'}
                        dataType="string"
                        allowEditing={false}
                    />
                    <Column
                        dataField={'uom'}
                        caption={'UOM'}
                        dataType="string"
                        allowEditing={false}
                    />
                    <Column
                        dataField={'quantity'}
                        caption={'Qty'}
                        dataType="number"
                        editorOptions={{
                            showSpinButtons: true,
                            min: 0,
                            format: "##0.###"
                        }}
                        showEditorAlways={true}
                        width={80}
                        cssClass='required'
                    >
                    </Column>
                    <Column
                        dataField={'pullFromStock'}
                        caption={'Stock'}
                        dataType="boolean"
                        showEditorAlways={true}
                    />
                    <Column
                        dataField={'backOrdered'}
                        caption={'Back Ordered'}
                        dataType="boolean"
                        allowEditing={false}
                    />
                    <Column
                        dataField={'unitCost'}
                        caption={'Unit Cost'}
                        dataType="number"
                        allowEditing={false}
                    >
                        <Format type="currency" precision={2} ></Format>
                    </Column>
                    <Column
                        dataField={'totalCost'}
                        caption={'Total Cost'}
                        dataType="number"
                        allowEditing={false}
                        calculateCellValue={(row: MOProduct) => { return row.pullFromStock ? 0 : row.unitCost * row.quantity }}
                    >
                        <Format type="currency" precision={2} ></Format>
                    </Column>
                    <Column
                        dataField={'productRequestedDeliveryDate'}
                        caption={'Requested Delivery Date'}
                        dataType="date"
                        showEditorAlways={true}
                        editorOptions={{ showClearButton: false }}
                        width={100}
                        cssClass='required'
                    />
                </DataGrid>
                <div className='dx-clearfix'>
                    <table className='summary-table'>
                        <tbody>
                            <tr>
                                <td>Subtotal:</td>
                                <td>{formatNumber(order.details.subTotal, { type: 'currency', precision: 2 })}</td>
                            </tr>
                            <tr>
                                <td>Cut Charge:</td>
                                <td>{formatNumber(order.details.cutCharges, { type: 'currency', precision: 2 })}</td>
                            </tr>
                            <tr>
                                <td>Freight:</td>
                                <td>{formatNumber(order.details.freightCharges, { type: 'currency', precision: 2 })}</td>
                            </tr>
                            <tr>
                                <td>Tax Rate:</td>
                                <td>{formatNumber(order.details.taxRate ?? 0, { type: 'percent', precision: 2 })}</td>
                            </tr>
                            <tr>
                                <td>Tax Amount:</td>
                                <td>{formatNumber(order.details.taxAmount ?? 0, { type: 'currency', precision: 2 })}</td>
                            </tr>
                            <tr className='total'>
                                <td>Total:</td>
                                <td>{formatNumber(order.details.amount, { type: 'currency', precision: 2 })}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}
